import taggingDataJson from 'data/FoodTaggingInfo.json';
import moment from 'moment';
import type { SemanticCOLORS } from 'semantic-ui-react';
import type { NutritionInfo } from 'types';

import { getIncludeMatches, nextWeekDay, removeMatches } from '../../utils/utils';

import { VENDORS, VENDORS_ORDER_CUTOFFDAY } from './vendorConfig';

// Lookups key this by a runtime tag string off an order, not by the literal keys
// `resolveJsonModule` infers, so the index signature is what makes the reads resolve.
const taggingData = taggingDataJson as typeof taggingDataJson & {
  allergens: Record<string, string[] | undefined>;
  'non-allergens': Record<string, string[] | undefined>;
};

const nutritionInfoColors = {
  green: 'teal' as SemanticCOLORS,
  yellow: 'orange' as SemanticCOLORS,
  red: 'red' as SemanticCOLORS,
  blue: 'blue' as SemanticCOLORS,
};

const requiredNutrition = ['sodium', 'addedSugars', 'totalCarbs', 'totalCarbohydrates', 'netCarbs'];
const nutritionUnits = { sodium: 'mg', addedSugars: 'g', totalCarbs: 'g', totalCarbohydrates: 'g', netCarbs: 'g' };
const nutritionTag = {
  sodium: 'Sodium',
  addedSugars: 'Added Sugars',
  totalCarbs: 'Net Carbs',
  totalCarbohydrates: 'Net Carbs',
  netCarbs: 'Net Carbs',
};

function isRequiredNutrient(nutrition: string): nutrition is RequiredNutrient {
  return requiredNutrition.indexOf(nutrition) !== -1;
}

/** The nutrient keys `nutritionTag`, `nutritionUnits` and `foodNutrition` recognise. */
type RequiredNutrient = keyof typeof nutritionTag;

/**
 * A vendor's raw nutrition payload. Keys are vendor-shaped — SB sends `sodium`, PK
 * and MH send `sodium (mg)`, FF sends `dietaryFiber(g)` — which is why every parser
 * derives its lookup key from the raw key rather than reading a named field.
 *
 * Values are `string` alone, narrower than the `number | string` on
 * `FoodItem.nutritionInfo`, because the parsers call `.indexOf` and `.split` on them
 * unguarded: a numeric value throws `TypeError: …indexOf is not a function`.
 */
type VendorNutritionData = Record<string, string>;

// Sunbasket
function parseSBNutritionInfo(nutritionData: VendorNutritionData): NutritionInfo[] {
  const nutritionList: NutritionInfo[] = [];
  Object.keys(nutritionData).forEach((nutrition) => {
    let quantity: number | string = 0;
    if (isRequiredNutrient(nutrition)) {
      const nutritionValue = nutritionData[nutrition]!;
      // let b = nutritionValue.indexOf("(")!==-1? nutritionValue.split("(")[0] : nutritionValue;
      if (nutritionValue.indexOf(nutritionUnits[nutrition]) !== -1) {
        quantity = nutritionValue.split(nutritionUnits[nutrition])[0]!;
      }

      if (nutrition === 'totalCarbs' && nutritionData['dietaryFiber']) {
        const nutritionValue = nutritionData['dietaryFiber'];
        let dietaryFiber: number | string = 0;
        // let b = nutritionValue.indexOf("(")!==-1? nutritionValue.split("(")[0] : nutritionValue;
        if (nutritionValue.indexOf(nutritionUnits[nutrition]) !== -1) {
          dietaryFiber = nutritionValue.split(nutritionUnits[nutrition])[0]!;
        }
        quantity = calculateNetCarbs(quantity, dietaryFiber);
      }

      const nutritionInfo = {
        name: nutritionTag[nutrition],
        unit: nutritionUnits[nutrition],
        color: foodNutrition(nutrition, quantity),
        quantity: quantity,
      };
      nutritionList.push(nutritionInfo);
    }
  });
  return nutritionList;
}

/**
 * SUSPECTED DEFECT — REPORTED, NOT FIXED. The nutrient colour band is inverted for
 * every MH item whose feed key carries a unit suffix.
 *
 * The `isRequiredNutrient` guard vets `nutritionKey` — the key with its `(unit)`
 * suffix stripped — but the reads below it index `nutritionUnits` with the RAW
 * `nutrition`. MH's feed sends `sodium (mg)`, so the lookup is `undefined` on exactly
 * the keys this branch is reached for; `indexOf(undefined)` then searches for the
 * literal `"undefined"`, misses, and leaves `quantity` at `0`.
 *
 * Measured, sodium `'900mg'`: key `'sodium (mg)'` yields `color: 'teal'`, key
 * `'sodium'` yields `color: 'red'`. 900mg is above the 750 red threshold, so the real
 * feed shape paints an over-threshold sodium value GREEN — the opposite of the
 * intended warning. The displayed number is unaffected; the pushed `quantity` is
 * `nutritionValue`, not the miscomputed local.
 *
 * Passing `nutritionKey` to both lookups would repaint every MH nutrient label.
 */
function parseMHNutritionInfo(nutritionData: VendorNutritionData): NutritionInfo[] {
  const nutritionList: NutritionInfo[] = [];
  Object.keys(nutritionData).forEach((nutrition) => {
    let quantity: number | string = 0;
    let nutritionKey = nutrition;
    if (nutrition.indexOf('(') !== -1) {
      nutritionKey = nutrition.split('(')[0]!.trim();
    }

    if (isRequiredNutrient(nutritionKey)) {
      const nutritionValue = nutritionData[nutrition]!;
      // let b = nutritionValue.indexOf("(")!==-1? nutritionValue.split("(")[0] : nutritionValue;
      const rawUnit = nutritionUnits[nutrition as RequiredNutrient] as string | undefined;
      if (nutritionValue.indexOf(rawUnit as string) !== -1) {
        quantity = nutritionValue.split(rawUnit as string)[0]!;
      }

      // No need to tag in case of total carbs. Net carbs already available in the nutrition info provided by MH
      if (nutrition.indexOf('totalCarbs') === -1) {
        const nutritionInfo = {
          name: nutritionTag[nutritionKey],
          unit: nutritionUnits[nutritionKey],
          color: foodNutrition(nutritionKey, quantity),
          quantity: nutritionValue,
        };
        nutritionList.push(nutritionInfo);
      }
    }
  });
  return nutritionList;
}

// Performance Kitchen : Nutrition parsing format: sodium (mg): "590"
function parsePKNutritionInfo(nutritionData: VendorNutritionData): NutritionInfo[] {
  const nutritionList: NutritionInfo[] = [];
  Object.keys(nutritionData).forEach((nutritionKey) => {
    let quantity: number | string = nutritionData[nutritionKey]!;
    let nutrition = '';
    if (nutritionKey.indexOf('(') !== -1) {
      nutrition = nutritionKey.split('(')[0]!.trim();
    } else {
      nutrition = nutritionKey;
    }

    if (nutrition.indexOf('totalCarbs') !== -1) {
      const key = Object.keys(nutritionData).filter((key) => key.includes('dietaryFiber'));

      if (key.length > 0) {
        const dietaryFiberKey = key[0]!;
        const dietaryFiber = nutritionData[dietaryFiberKey]!;
        quantity = calculateNetCarbs(quantity, dietaryFiber);
      }
    }

    if (isRequiredNutrient(nutrition)) {
      const nutritionInfo = {
        name: nutritionTag[nutrition],
        unit: nutritionUnits[nutrition],
        color: foodNutrition(nutrition, quantity),
        quantity: quantity,
      };
      nutritionList.push(nutritionInfo);
    }
  });
  return nutritionList;
}

// Fork & Function : Nutrition parsing format: sodium (mg): "590"
function parseFFNutritionInfo(nutritionData: VendorNutritionData): NutritionInfo[] {
  const nutritionList: NutritionInfo[] = [];
  Object.keys(nutritionData).forEach((nutritionKey) => {
    let quantity: number | string = nutritionData[nutritionKey]!;
    let nutrition = '';
    if (nutritionKey.indexOf('(') !== -1) {
      nutrition = nutritionKey.split('(')[0]!.trim();
    } else {
      nutrition = nutritionKey;
    }

    if (nutrition === 'totalCarbohydrates' && nutritionData['dietaryFiber(g)']) {
      const dietaryFiber = nutritionData['dietaryFiber(g)'];
      quantity = calculateNetCarbs(quantity, dietaryFiber);
    }

    if (isRequiredNutrient(nutrition)) {
      const nutritionInfo = {
        name: nutritionTag[nutrition],
        unit: nutritionUnits[nutrition],
        color: foodNutrition(nutrition, quantity),
        quantity: quantity,
      };
      nutritionList.push(nutritionInfo);
    }
  });
  return nutritionList;
}

function _parseMFNutritionInfo(_itemDetail: unknown): undefined {
  // TO DO. Data is not available yet
}

//Albertson's
function _parseABNutritionInfo(_itemDetail: unknown): undefined {
  // TO DO. Data is not available yet
}

/**
 * The Semantic colour band for a nutrient value.
 *
 * `undefined` on two paths the callers do not distinguish: an unrecognised nutrient,
 * and a value that falls through a band's comparisons — `sodium` has no arm for
 * `NaN`, which is what `parseInt` yields for a value that is not numeric text.
 */
function foodNutrition(nutrition: string, quantity: number | string): SemanticCOLORS | undefined {
  if (typeof quantity === 'string') {
    quantity = parseInt(quantity);
  }

  switch (nutrition) {
    case 'sodium':
      if (quantity <= 650) {
        return nutritionInfoColors.green;
      } else if (quantity > 650 && quantity <= 750) {
        return nutritionInfoColors.yellow;
      } else if (quantity > 750) {
        return nutritionInfoColors.red;
      }
      break;
    case 'addedSugars':
      if (quantity < 10) {
        return nutritionInfoColors.green;
      } else if (quantity >= 10) {
        return nutritionInfoColors.red;
      }
      break;
    case 'totalCarbs':
      if (quantity >= 30 && quantity <= 60) {
        return nutritionInfoColors.green;
      } else if (quantity < 30) {
        return nutritionInfoColors.yellow;
      } else if (quantity > 60) {
        return nutritionInfoColors.red;
      }
      break;
    // For FF
    case 'totalCarbohydrates':
      if (quantity >= 30 && quantity <= 60) {
        return nutritionInfoColors.green;
      } else if (quantity < 30) {
        return nutritionInfoColors.yellow;
      } else if (quantity > 40) {
        return nutritionInfoColors.red;
      }
      break;
    case 'netCarbs':
      if (quantity >= 30 && quantity <= 60) {
        return nutritionInfoColors.green;
      } else if (quantity < 30) {
        return nutritionInfoColors.yellow;
      } else if (quantity > 40) {
        return nutritionInfoColors.red;
      }
      break;
  }
}

/** Only SB, PK, FF and MH have a parser; every other vendor yields `[]`. */
export function VendorNutritionInfo(nutritionData: VendorNutritionData, vendor: string): NutritionInfo[] {
  if (Object.keys(nutritionData).length) {
    if (vendor === VENDORS.SB) {
      return parseSBNutritionInfo(nutritionData);
    } else if (vendor === VENDORS.PK) {
      return parsePKNutritionInfo(nutritionData);
    } else if (vendor === VENDORS.FF) {
      return parseFFNutritionInfo(nutritionData);
    } else if (vendor === VENDORS.MH) {
      return parseMHNutritionInfo(nutritionData);
    } else {
      return [];
    }
  } else {
    return [];
  }
}
// Total Net Carbs = Total Carbs - Dietary Fiber
function calculateNetCarbs(carbs: number | string, dietaryFiber: number | string): number {
  if (typeof carbs === 'string') {
    carbs = parseInt(carbs);
  }
  if (typeof dietaryFiber === 'string') {
    dietaryFiber = parseInt(dietaryFiber);
  }

  const netCarbs = carbs - dietaryFiber;
  return netCarbs;
}

/**
 * `allergensData` is `unknown` because the two live branches take incompatible
 * shapes: PK sends a comma-separated string, SB sends an array passed straight
 * through. MF, GB, AB and NM have no feed data yet and yield `[]`.
 */
export function VendorAllergensInfo(allergensData: unknown, vendor: string): string[] {
  let allergiesList: string[] = [];
  if (allergensData && allergensData !== '' && allergensData !== '-') {
    switch (vendor) {
      case 'PK':
        allergiesList = (allergensData as string).split(',');
        break;
      case 'SB':
        allergiesList = allergensData as string[];
        break;

      case 'MF':
        // To Do. Data is not available yet
        break;

      case 'GB':
        // To Do. Data is not available yet
        break;

      case 'AB':
        // To Do. Data is not available yet
        break;

      case 'NM':
        // To Do. Data is not available yet
        break;
    }
  }
  return allergiesList;
}
/** The four fields of `FoodItem` the tagging helpers below actually read. */
type TaggableFoodItem = {
  description?: string | undefined;
  docId?: string | undefined;
  ingredients?: string[] | undefined;
  name?: string | undefined;
};

function getSoupsList(soupsData: string | undefined): string[] {
  const soupsList: string[] = [];
  if (soupsData?.toLowerCase().indexOf('soup') !== -1) {
    soupsList.push('Soup');
  }
  if (soupsData?.toLowerCase().indexOf('stew') !== -1) {
    soupsList.push('Stew');
  }
  if (soupsData?.toLowerCase().indexOf('chili') !== -1 || soupsData?.toLowerCase().indexOf('chile') !== -1) {
    soupsList.push('Chili');
  }
  return soupsList;
}

/**
 * Soup/stew/chili labels derived from a PK `docId` or an SB `description`.
 *
 * `undefined`, not `[]`, for MF, GB, AB and any unrecognised vendor — those `switch`
 * arms `break` without returning. `[]` comes back only when the item has neither a
 * description nor a `docId`.
 */
export function VendorSoupsInfo(item: TaggableFoodItem | undefined, vendor: string): string[] | undefined {
  let soupsData: string | undefined = '';
  if (item && ((item.description && item.description !== '') || item.docId)) {
    switch (vendor) {
      case 'PK':
        soupsData = item.docId;
        return getSoupsList(soupsData);
      case 'SB':
        soupsData = item.description;
        return getSoupsList(soupsData);
      case 'MF':
        // To Do. Data is not available yet
        break;
      case 'GB':
        // To Do. Data is not available yet
        break;
      case 'AB':
        // To Do. Data is not available yet
        break;
    }
  } else {
    return [];
  }
}

/**
 * Whether any of the member's stated preferences or avoidances matches this item.
 *
 * Returns `boolean` from every vendor arm, but `[]` when the item has neither a
 * description nor a `docId` — and `[]` is truthy at `foodItemCard.tsx:264`, so a
 * description-less item reads as "preference matched".
 *
 * SUSPECTED DEFECT — REPORTED, NOT FIXED. Two unguarded dereferences, both TS18048:
 *
 * `item.name.split(' ')` runs before any vendor branch and before the guard, so an
 * item with no `name` throws on entry regardless of vendor.
 *
 * `item.docId.toLowerCase()` appears in all six vendor arms. The guard above them
 * admits an item with a non-empty `description` and NO `docId` — the SB shape, which
 * is why `VendorSoupsInfo` reads `description` for SB. That item raises
 * `TypeError: Cannot read properties of undefined (reading 'toLowerCase')`.
 *
 * Adding `?.` to either converts the throw into a silent `false`, changing which
 * items get tagged.
 */
export function PatientObservationsInFoodItem(
  item: TaggableFoodItem,
  vendor: string,
  observation: string[],
  _tagsFoodItems?: string[]
): boolean | never[] {
  let observationFound = false;
  const itemDescription = item.description;

  const splitItemName: string[] = item.name!.split(' ');
  // Every use below is behind an `itemIngredients ? … : ['na']` ternary, so absence
  // is the expected case for the produce vendors, whose items carry no ingredient list.
  const itemIngredients: string[] | undefined = item?.ingredients;
  const extraTags: string[] = observation

    //find matching allergens to tags ie: Dairy, Tree Nuts, Fish
    .map((allergen) => {
      let foundTag: string;
      //Don't worry about allergens labelling.
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      Object.prototype.hasOwnProperty.call(taggingData.allergens, allergen) ? (foundTag = allergen) : (foundTag = '');
      return foundTag;
    })
    //remove blank entries
    .filter((a) => {
      return a !== '';
    });
  //pull entire list of tags from json file
  const allAllergensFromTags: (string | undefined)[] = extraTags.flatMap((a: string) => {
    return taggingData.allergens[a];
  });
  //expempt allergens pull from json file
  const exemptIngredients: string[] = extraTags
    ? extraTags
        .flatMap((a: string) => {
          return taggingData['non-allergens'][a];
        }) //remove blank entries
        .filter((a: string | undefined) => {
          return a !== '';
        })
        .filter((a: string | undefined): a is string => {
          return a !== undefined;
        })
    : [''];
  const falseIngredientss: string[] = itemIngredients
    ? getIncludeMatches(
        itemIngredients ?? ['na'],
        exemptIngredients.filter((a: string | undefined) => {
          return a !== undefined;
        })
      )?.map((fa) => fa.toUpperCase())
    : ['na'];
  const falseItemNameWords: string[] = splitItemName
    ? getIncludeMatches(splitItemName, exemptIngredients, ' ').map((fa) => fa.toUpperCase())
    : ['na'];
  const cleanedIngrediantList: string[] = itemIngredients
    ? [...removeMatches(falseIngredientss, itemIngredients)]
    : ['na'];
  const cleanedItemName: string[] = [...removeMatches(falseItemNameWords, splitItemName, ' ')];

  if (item && ((item.description && item.description !== '') || item.docId)) {
    switch (vendor) {
      case 'PK':
      case 'FFH':
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        observation &&
          observation.forEach((preference) => {
            const matchingAllergies = getIncludeMatches(cleanedIngrediantList, allAllergensFromTags);
            if (matchingAllergies?.length > 0) {
              observationFound = true;
            }
            const matchingIngredients = cleanedIngrediantList.find((i) => {
              return i?.toLowerCase().indexOf(preference?.toLowerCase()) !== -1;
            });

            if (cleanedIngrediantList && matchingIngredients) {
              observationFound = true;
            }
            //
            if (item.docId!.toLowerCase().indexOf(preference?.toLowerCase()) !== -1) {
              observationFound = true;
            }
          });

        return observationFound;
      case 'SB':
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        observation &&
          observation.forEach((preference) => {
            //
            if (itemDescription?.toLowerCase().indexOf(preference?.toLowerCase()) !== -1) {
              observationFound = true;
            }
            //
            if (item.docId!.toLowerCase().indexOf(preference?.toLowerCase()) !== -1) {
              observationFound = true;
            }
          });
        return observationFound;
      case 'MF':
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        observation &&
          observation.forEach((preference) => {
            //
            //
            const matchingAllergies = getIncludeMatches(cleanedItemName, allAllergensFromTags);
            if (matchingAllergies?.length > 0) {
              observationFound = true;
            }
            if (item.docId!.toLowerCase().indexOf(preference?.toLowerCase()) !== -1) {
              observationFound = true;
            }
            if (cleanedItemName.indexOf(preference?.toLowerCase()) !== -1) {
              observationFound = true;
            }
          });
        return observationFound;
      case 'GB':
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        observation &&
          observation.forEach((preference) => {
            //
            //
            const matchingAllergies = getIncludeMatches(cleanedItemName, allAllergensFromTags);
            if (matchingAllergies?.length > 0) {
              observationFound = true;
            }
            if (item.docId!.toLowerCase().indexOf(preference?.toLowerCase()) !== -1) {
              observationFound = true;
            }
            if (cleanedItemName.indexOf(preference?.toLowerCase()) !== -1) {
              observationFound = true;
            }
          });
        return observationFound;
      case 'AB':
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        observation &&
          observation.forEach((preference) => {
            //
            //
            const matchingAllergies = getIncludeMatches(cleanedItemName, allAllergensFromTags);
            if (matchingAllergies?.length > 0) {
              observationFound = true;
            }
            if (item.docId!.toLowerCase().indexOf(preference?.toLowerCase()) !== -1) {
              observationFound = true;
            }
            if (cleanedItemName.indexOf(preference?.toLowerCase()) !== -1) {
              observationFound = true;
            }
          });
        return observationFound;
      case 'NM':
        // To Do. Data is not available yet
        return observationFound;
      default:
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        observation &&
          observation.forEach((preference) => {
            //
            const matchingIngredients = cleanedIngrediantList
              ? cleanedIngrediantList.find((i) => {
                  return i?.toLowerCase().indexOf(preference?.toLowerCase()) !== -1;
                })
              : undefined;
            if (
              //check item ingredients for
              cleanedIngrediantList &&
              matchingIngredients
            ) {
              observationFound = true;
            }
            //
            if (item.docId!.toLowerCase().indexOf(preference?.toLowerCase()) !== -1) {
              observationFound = true;
            }
          });
        //todo: return the list of matching ingredients instead of boolean
        return observationFound;
    }
  } else {
    return [];
  }
}

/**
 * `vendor` is accepted but never read — the matching is identical for every vendor.
 *
 * SUSPECTED DEFECT — REPORTED, NOT FIXED, already pinned by `vendorUtils.spec.tsx:207`
 * and `:218`. The second loop tests each entry of `foodItemAllergens` against
 * `allergensString`, which is that same array joined — so every entry matches itself,
 * and any item declaring ANY allergen reports `true` regardless of the member's
 * actual allergies.
 */
export function PatientAllergiesInFoodItem(
  patientAllergies: string[] | undefined, //from observations
  foodItem: { docId?: string; name?: string }, //bundle or produce item name
  vendor: string,
  foodItemAllergens: string[], //array of possible allergens,
  _tagsFoodItems?: string[]
): boolean {
  let allergiesFound = false;

  // For Icons Tagging - Begin

  if (patientAllergies && patientAllergies.length) {
    let allergensString = '';
    if (foodItemAllergens && foodItemAllergens.length) {
      allergensString = foodItemAllergens.join('|');
    }
    //compare as is to patientAllergeis list
    patientAllergies.forEach((allergy) => {
      if (allergensString !== '' && allergensString?.toLowerCase().indexOf(allergy?.toLowerCase()) !== -1) {
        allergiesFound = true;
      }
      if (
        (foodItem.docId && foodItem.docId.toLowerCase().indexOf(allergy?.toLowerCase()) !== -1) ||
        (foodItem.name && foodItem.name.toLowerCase().indexOf(allergy?.toLowerCase()) !== -1)
      ) {
        allergiesFound = true;
      }
    });
    //compare to foodItemAllergens list
    foodItemAllergens.forEach((allergy) => {
      if (allergensString !== '' && allergensString?.toLowerCase().indexOf(allergy?.toLowerCase()) !== -1) {
        allergiesFound = true;
      }
    });
  }

  return allergiesFound;
}

/**
 * Returns the module-level `VENDORS_ORDER_CUTOFFDAY` ITSELF, having assigned onto it
 * — not a copy. A flag set by one call is visible to every later caller, including
 * callers asking about a different vendor. Pinned by the "shared mutable cutoff
 * object" block in `vendorUtils.spec.tsx`; only the SB arm writes, so only SB leaks.
 */
export function CheckOrderCutoff(
  bundle: { deadline?: string; vendorId?: string } | undefined,
  orderDate: moment.MomentInput
): typeof VENDORS_ORDER_CUTOFFDAY {
  const cutOff = VENDORS_ORDER_CUTOFFDAY;
  if (bundle && bundle.vendorId) {
    try {
      switch (bundle.vendorId) {
        case 'PK':
          // To Do.
          break;
        case 'SB': {
          const vendorDeadlineDate = moment(bundle?.deadline);
          //return ;
          const flag = moment(orderDate).isAfter(vendorDeadlineDate);
          cutOff['SB']['flag'] = flag;
          cutOff['SB']['message'] = flag ? 'Menu is past deadline. Order creation prohibited.' : '';
          break;
        }
        case 'MF':
          // let cutOffDate = nextWeekDay(orderDate,VENDORS_ORDER_CUTOFFDAY.MF.cutOffDay);
          // cutOff["MF"]["flag"] =  false;
          // cutOff["MF"]["message"] =  "Order CutOff Date is: "+cutOffDate.format("MM/DD/YYYY");
          break;
        case 'GB':
          break;
        case 'AB':
          break;
      }
      return cutOff;
    } catch (e) {
      console.log(e);
      return cutOff;
    }
  } else {
    return cutOff;
  }
}

/**
 * The date a task against this vendor should start: the order date for most, the
 * next weekday cutoff for MF and AB. Like `CheckOrderCutoff`, both computing arms
 * assign onto the shared `VENDORS_ORDER_CUTOFFDAY`.
 *
 * SUSPECTED DEFECT — REPORTED, NOT FIXED. The `.format(...)` calls on `cutOffDate`
 * and `abCutOffDate` are unguarded, and `nextWeekDay` in `utils/utils` returns
 * `undefined` whenever `dayINeed` is `''` — its `if` has no `else`. MF and AB carry
 * real weekday codes today, so this does not fire; it fires the moment either is
 * cleared to `''`, the value every other vendor in the table already has.
 *
 * The failure would be silent: the enclosing `catch` swallows the `TypeError`, logs,
 * and returns `orderDate`, so the caller gets a plausible date and the cutoff is
 * simply not applied.
 */
export function GetTasksStartDate(vendorId: string, orderDate: moment.MomentInput): moment.Moment | moment.MomentInput {
  const cutOff = VENDORS_ORDER_CUTOFFDAY;

  try {
    switch (vendorId) {
      case 'PK':
        // To Do.

        return orderDate;
      case 'SB':
        // var vendorDeadlineDate = moment(bundle.deadline);
        // //return ;
        // let flag = moment(orderDate).isAfter(vendorDeadlineDate);
        // cutOff["SB"]["flag"] =  flag;
        // cutOff["SB"]["message"] =  flag ? "Menu is past deadline. Order creation prohibited." : "";
        return orderDate;

      case 'MF': {
        const cutOffDate = nextWeekDay(orderDate, VENDORS_ORDER_CUTOFFDAY.MF.cutOffDay);

        cutOff['MF']['flag'] = false;
        cutOff['MF']['message'] = 'Order CutOff Date is: ' + cutOffDate!.format('MM/DD/YYYY');
        return cutOffDate;
      }

      case 'AB': {
        const abCutOffDate = nextWeekDay(orderDate, VENDORS_ORDER_CUTOFFDAY.AB.cutOffDay);

        cutOff['AB']['flag'] = false;
        cutOff['AB']['message'] = 'Order CutOff Date is: ' + abCutOffDate!.format('MM/DD/YYYY');
        return abCutOffDate;
      }

      default:
        return orderDate;
    }
  } catch (e) {
    console.log(e);
    return orderDate;
  }
}
